import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Stethoscope, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Calendar,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import { format } from 'date-fns';

const ServiceUsageHistory = ({ member }) => {
  const [usageSummary, setUsageSummary] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (member?.id) {
      fetchUsageData();
    } else {
      setLoading(false);
    }
  }, [member]);

  const fetchUsageData = async () => {
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/clinic/services/usage/member/${member.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/clinic/services/usage/history/${member.id}`).catch(() => ({ data: [] }))
      ]);
      setUsageSummary(summaryRes.data || []);
      setUsageHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      setUsageSummary([]);
      setUsageHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used, allowed) => {
    if (allowed === -1) return 0; // Unlimited
    if (allowed === 0) return 100;
    return Math.min((used / allowed) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-teal-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Services</p>
                <p className="text-2xl font-bold text-gray-900">{usageSummary.length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Services Used</p>
                <p className="text-2xl font-bold text-blue-600">
                  {usageSummary.reduce((acc, s) => acc + s.quantity_used, 0)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Remaining</p>
                <p className="text-2xl font-bold text-green-600">
                  {usageSummary.reduce((acc, s) => {
                    if (s.quantity_remaining === -1) return acc + 999;
                    return acc + s.quantity_remaining;
                  }, 0) > 999 ? '∞' : usageSummary.reduce((acc, s) => {
                    if (s.quantity_remaining === -1) return acc;
                    return acc + s.quantity_remaining;
                  }, 0)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Service Usage by Plan</CardTitle>
          <CardDescription>Status of services included in your membership</CardDescription>
        </CardHeader>
        <CardContent>
          {usageSummary.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No services assigned to your plan</p>
              <p className="text-sm text-gray-400 mt-1">Contact your clinic for more information</p>
            </div>
          ) : (
            <div className="space-y-4">
              {usageSummary.map((service) => {
                const percentage = getUsagePercentage(service.quantity_used, service.quantity_allowed);
                const isUnlimited = service.quantity_allowed === -1;
                const isLow = !isUnlimited && service.quantity_remaining <= 1 && service.quantity_remaining > 0;
                const isExhausted = !isUnlimited && service.quantity_remaining <= 0;

                return (
                  <div key={service.service_id} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{service.service_name}</h4>
                        <p className="text-sm text-gray-500">
                          Category: {service.category}
                        </p>
                      </div>
                      <div className="text-right">
                        {isUnlimited ? (
                          <Badge className="bg-green-100 text-green-800">Unlimited</Badge>
                        ) : isExhausted ? (
                          <Badge className="bg-red-100 text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Exhausted
                          </Badge>
                        ) : isLow ? (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Low
                          </Badge>
                        ) : (
                          <Badge className="bg-teal-100 text-teal-800">Available</Badge>
                        )}
                      </div>
                    </div>

                    {!isUnlimited && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Used: {service.quantity_used} of {service.quantity_allowed}
                          </span>
                          <span className="font-medium text-gray-900">
                            Remaining: {service.quantity_remaining}
                          </span>
                        </div>
                        <Progress value={percentage} className={`h-2 ${getProgressColor(percentage)}`} />
                      </div>
                    )}

                    {isUnlimited && (
                      <div className="text-sm text-gray-600">
                        <span>Services used: {service.quantity_used}</span>
                      </div>
                    )}

                    <div className="mt-2 text-xs text-gray-400">
                      Period: {service.period_start} - {service.period_end}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage History */}
      {usageHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Usage History</CardTitle>
            <CardDescription>Detailed record of services used</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usageHistory.slice(0, 10).map((usage) => (
                <div key={usage.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{usage.service_name || 'Service'}</p>
                      <p className="text-sm text-gray-500">
                        {usage.usage_date ? format(new Date(usage.usage_date), 'PPP') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {usage.appointment_id && (
                    <Badge variant="outline">
                      <Calendar className="h-3 w-3 mr-1" />
                      Appointment
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServiceUsageHistory;
